'use client';

import { type SegmentIdInput, normalizeSegmentIds } from '@/helper/personalization/normalizeSegmentId';
import { isStoryblokEditorContext } from '@/helper/storyblok/isStoryblokEditorContext';
import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';

type PersonalizationContextValue = {
  effectiveSegmentIds: string[];
};

const PersonalizationContext = createContext<PersonalizationContextValue>({ effectiveSegmentIds: [] });

export function PersonalizationProvider({
  children,
  activeUserSegments,
}: {
  children: ReactNode;
  activeUserSegments?: SegmentIdInput;
}) {
  const [simulationEnabled, setSimulationEnabled] = useState(
    () => typeof window !== 'undefined' && isStoryblokEditorContext({ includeDevelopment: true })
  );

  useEffect(() => {
    setSimulationEnabled(isStoryblokEditorContext({ includeDevelopment: true }));
  }, []);

  const effectiveSegmentIds = useMemo(() => {
    // CDP (später): in Prod CDP-Segment-IDs mergen; ohne Simulation nicht pauschal [].
    if (!simulationEnabled) {
      return [];
    }
    return normalizeSegmentIds(activeUserSegments);
  }, [simulationEnabled, activeUserSegments]);

  const value = useMemo(() => ({ effectiveSegmentIds }), [effectiveSegmentIds]);

  return <PersonalizationContext.Provider value={value}>{children}</PersonalizationContext.Provider>;
}

/** CDP (später): Konsument für aufgelöste Segment-IDs; Quelle wird um CDP erweitert. */
export function useEffectiveUserSegmentIds(): string[] {
  return useContext(PersonalizationContext).effectiveSegmentIds;
}
